import { Alert, Button, Card, TextField } from '@mui/material';
import { deleteUser, EmailAuthProvider, getAuth, reauthenticateWithCredential } from 'firebase/auth';
import { doc, getFirestore, writeBatch } from 'firebase/firestore';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { handleError } from '../../../common/firebase/auth-error.service';
import { FirestoreService } from '../../../common/firebase/firestore.service';
import { severity } from '../../../common/models/severity.enum';
import { Building, Project, Site } from '../../../confi-state';
import { isProdOrDevEnv } from '../../../environment.enum';
import { firestoreCollections } from '../../../services/firestore-collections.enum';
import { ServiceManager } from '../../../services/service-manager';
import styles from './DeleteAccount.module.css';

interface DeleteAccountProps {
    user: any
}

const DeleteAccount: FC<DeleteAccountProps> = ({ user }) => {

    const firstRenderRef = useRef(true);
    const [projects, setProjects] = useState<Project[]>([]);
    const [projectsLoaded, setProjectsLoaded] = useState<boolean>(false);
    const [needsReauth, setNeedsReauth] = useState<boolean>(false);

    const { register, handleSubmit, formState: { errors } } = useForm<{ password: string }>();

    const navigate = useNavigate();

    useEffect(() => {
        if (!isProdOrDevEnv() && firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }
        const load = async () => {
            const p = await FirestoreService.getCollectionForUserId<Project>(firestoreCollections.projects, user.uid, (err) => {
                handleError(err);
            }, true);

            setProjects(p);
            setProjectsLoaded(true);
        }

        load();
    }, []);

    const deleteProjects = async () => {

        // TODO delete assets (background image for site if exists)

        const db = getFirestore();

        let nrBuildings = 0;
        let nrSites = 0;
        let nrProject = projects.length;

        Promise.all(projects.map(async p => {
            const sitesInProject = await FirestoreService.getCollectionForUserId<Site>(`${firestoreCollections.projects}/${p.id}/${firestoreCollections.sites}`, user.uid,
                (err) => {
                    handleError(err);
                }, true);

            const buildingsInProject = await Promise.all(
                sitesInProject.map(async s => {
                    const buildingsInSite = await FirestoreService.getCollectionForUserId<Building>(`${firestoreCollections.projects}/${p.id}/${firestoreCollections.sites}/${s.id}/buildings`, user.uid,
                        (err) => {
                            handleError(err);
                        }, true);

                    return Promise.all(buildingsInSite);
                })
            );

            const flatBuildingsInProject = buildingsInProject.flat();

            const buildingBatch = writeBatch(db);

            nrBuildings += flatBuildingsInProject.length;

            flatBuildingsInProject.forEach(b => {
                const buildingDocRef = doc(db, `projects/${b.projectId}/sites/${b.siteId}/buildings/${b.id}`);
                buildingBatch.delete(buildingDocRef);
            });

            const bbatchPromise = buildingBatch.commit();

            const sitesBatch = writeBatch(db);

            sitesInProject.forEach(s => {
                const siteDocRef = doc(db, `projects/${s.projectId}/sites/${s.id}`);
                sitesBatch.delete(siteDocRef);
            });

            nrSites += sitesInProject.length;

            const sbatchPromise = sitesBatch.commit();
            return Promise.all([bbatchPromise, sbatchPromise])
        }))
            .then(() => {
                const projectBatch = writeBatch(db);

                projects.forEach(p => {
                    const projectDocRef = doc(db, `projects/${p.id}`);
                    projectBatch.delete(projectDocRef);
                });

                projectBatch.commit().then(() => {
                    // CONTINUE TO SECOND STEP
                    ServiceManager.snackbarService.openSnackbar(`${nrBuildings} gebouw(en), ${nrSites} kavel(s) in ${nrProject} project(en) zijn succesvol verwijderd.`, severity.success);
                    setProjects([]);
                }).catch(err => {
                    handleError(err);
                });
                // FirestoreService.deleteFileOrFolder(`user-data/${user.uid}`).then(() => {

                // }).catch(err => showError(err));
            })
            .catch(err => {
                handleError(err);
            });
    }

    const deleteMe = () => {

        const auth = getAuth();
        const user = auth.currentUser;

        deleteUser(user).then(() => {
            ServiceManager.snackbarService.openSnackbar(`Uw account ${user.email} is verwijderd.`, severity.success);
            navigate('/auth/signup');
        }).catch((err) => {
            handleError(err);
            setNeedsReauth(true);
        });
    }

    const reauthenticate = async (data: { password: string }) => {
        const credential = EmailAuthProvider.credential(user.email, data.password);
        const auth = getAuth();
        reauthenticateWithCredential(auth.currentUser, credential).then(() => {
            setNeedsReauth(false);
        }).catch(err => {
            handleError(err);
        });
    }

    return (
        <div className={styles.DeleteAccount} data-testid="DeleteAccount">
            <div className={styles.base}>
                {needsReauth ?
                    <>
                        Om uw account te verwijderen hebben we uw wachtwoord nodig
                        <form data-testid="password-form" onSubmit={handleSubmit(reauthenticate)}>
                            <div>
                                <TextField id="password"
                                    fullWidth
                                    className="form-input"
                                    label="Uw Wachtwoord"
                                    {...register('password', { required: { value: true, message: 'U moet uw huidige wachtwoord invullen.' } })}
                                    variant="standard"
                                    type="password"
                                />
                                {errors.password && <p className="input-error">{errors.password.message}</p>}
                            </div>
                            <div className={styles.actions}>
                                <Button color="primary" data-testid="password-submit" size="small" variant="contained" type="submit">Wachtwoord Aangeven</Button>
                            </div>
                        </form>
                    </> :
                    <>
                        <Alert severity={severity.warning} sx={{ width: '100%' }}>
                            Let op, deze actie kan niet ongedaan gemaakt worden. We bewaren geen backup van uw data.
                        </Alert>
                        {projectsLoaded && projects.length > 0 &&
                            <div>
                                <div className={styles.projects}>
                                    <h2>U dient alle projecten te verwijderen</h2>
                                    <p>Projecten:</p>
                                    {
                                        projects.map((p, index) => <Card key={p.id} sx={{ margin: '5px', padding: '10px' }}>
                                            <b>{index + 1}: </b><b>{p.name}</b> / {p.archived ? 'Gearchiveerd' : 'Niet gearchiveerd'}
                                        </Card>)
                                    }
                                </div>
                                <div className={styles.actions}>
                                    <Button color="primary" variant="contained" onClick={deleteProjects}>{projects.length} Project(en) Verwijderen</Button>
                                </div>
                            </div>
                        }
                        {projectsLoaded && projects.length === 0 &&
                            <div>
                                <div className={styles.projects}>
                                    <h2>Verwijder uw account</h2>
                                    <p>
                                        Als deze actie mislukt, moet u opnieuw uw huidige wachtwoord aangeven. Alleen recent ingelogde gebruikers kunnen hun accounts verwijderen.
                                    </p>
                                </div>
                                <div className={styles.actions}>
                                    <Button color="error" variant="contained" onClick={deleteMe}>Account Verwijderen</Button>
                                </div>
                            </div>
                        }
                    </>
                }
            </div>
        </div>
    )
};

export default DeleteAccount;